import { pmsMetricsService } from '../services'

const initialState = () => ({
  summary: null,
  timeseries: null,
  readers: null,
  pathologies: null,
  status: {
    loading: false,
    loaded: false,
    error: null
  },
  fetchKey: null, // project|subgroup|recentWindowDays of the last fetch — dedups sibling components
  lastFetch: null
})

const state = initialState()

const actions = {
  async fetchAll ({ state, dispatch, commit }, { project, subgroup = null, recentWindowDays = 30, force = false }) {
    // Skip a redundant fetch when siblings request the same (project, subgroup, window)
    // that's already loading or loaded. `force` (used after recompute) bypasses this.
    const key = `${project}|${subgroup ?? ''}|${recentWindowDays}`
    if (!force && key === state.fetchKey && (state.status.loading || state.status.loaded)) return
    // Clear stale data once when the project actually changes (sibling calls then dedup-skip).
    const prevProject = state.fetchKey ? state.fetchKey.split('|')[0] : null
    if (prevProject && prevProject !== project) commit('clear')
    commit('setFetchKey', key)
    commit('setLoading')
    try {
      await Promise.all([
        dispatch('fetchSummary', { project, subgroup, recentWindowDays }),
        dispatch('fetchTimeseries', { project, subgroup, recentWindowDays }),
        dispatch('fetchReaders', { project, subgroup, recentWindowDays }),
        dispatch('fetchPathologies', { project, subgroup, recentWindowDays })
      ])
      commit('setLoaded')
    } catch (error) {
      commit('setError', error)
      dispatch('alert/error', error, { root: true })
    }
  },
  async fetchSummary ({ commit }, { project, subgroup = null, recentWindowDays = 30 }) {
    const data = await pmsMetricsService.getSummary(project, { subgroup, recentWindowDays })
    commit('setSummary', data)
  },
  async fetchTimeseries ({ commit }, { project, subgroup = null, recentWindowDays = 30 }) {
    const data = await pmsMetricsService.getTimeseries(project, { subgroup, recentWindowDays })
    commit('setTimeseries', data)
  },
  async fetchReaders ({ commit }, { project, subgroup = null, recentWindowDays = 30 }) {
    const data = await pmsMetricsService.getReaders(project, { subgroup, recentWindowDays })
    commit('setReaders', data)
  },
  async fetchPathologies ({ commit }, { project, subgroup = null, recentWindowDays = 30 }) {
    const data = await pmsMetricsService.getPathologies(project, { subgroup, recentWindowDays })
    commit('setPathologies', data)
  },
  async recompute ({ dispatch }, { project, force = false, recentWindowDays = 30, subgroup = null }) {
    await pmsMetricsService.recompute(project, { force, recentWindowDays })
    // Server data changed — force past the dedup guard.
    await dispatch('fetchAll', { project, subgroup, recentWindowDays, force: true })
  },
  clear ({ commit }) {
    commit('clear')
  }
}

const mutations = {
  setLoading (state) {
    state.status = { loading: true, loaded: false, error: null }
  },
  setLoaded (state) {
    state.status = { loading: false, loaded: true, error: null }
    state.lastFetch = new Date().toISOString()
  },
  setError (state, error) {
    state.status = { loading: false, loaded: false, error }
  },
  setFetchKey (state, key) {
    state.fetchKey = key
  },
  setSummary (state, data) {
    state.summary = data
  },
  setTimeseries (state, data) {
    state.timeseries = data
  },
  setReaders (state, data) {
    state.readers = data
  },
  setPathologies (state, data) {
    state.pathologies = data
  },
  clear (state) {
    Object.assign(state, initialState())
  }
}

export const pmsMetrics = {
  namespaced: true,
  state,
  actions,
  mutations
}
